import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import {
  evolucionCategoria,
  gastoPorCategoria,
  ingresosGastosPorMes,
} from '#components/mobile/informes/calculos';
import { fraseCategoria } from '#components/mobile/informes/CategoriaPage';
import {
  MiniBarras,
  TarjetaInforme,
} from '#components/mobile/informes/comunes';
import {
  TarjetaDiarioMes,
  TarjetaDiarioSemana,
} from '#components/mobile/informes/diario/TarjetasDiario';
import {
  COLOR_EDAD,
  fraseEdad,
  tonoEdad,
} from '#components/mobile/informes/EdadDineroPage';
import {
  clasificarMes,
  mediana,
} from '#components/mobile/informes/estadisticaRobusta';
import {
  COLOR_NOMINAS,
  fraseNominas,
} from '#components/mobile/informes/NominasPage';
import { useCategoriasExcluidas } from '#components/mobile/informes/useCategoriasExcluidas';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { createAgeOfMoneySpreadsheet } from '#components/reports/spreadsheets/age-of-money-spreadsheet';
import type { AgeOfMoneyData } from '#components/reports/spreadsheets/age-of-money-spreadsheet';
import { useReport } from '#components/reports/useReport';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';

/** «Tu semana»: la última semana cerrada del Diario de a bordo. */
export function TarjetaSemana(_: PropsWidget) {
  return <TarjetaDiarioSemana enRejilla />;
}

/** «Diario de a bordo»: el último mes cerrado, contado por Gavi. */
export function TarjetaDiario(_: PropsWidget) {
  return <TarjetaDiarioMes enRejilla />;
}

/**
 * «Una categoría»: la categoría con más gasto este mes frente a sus últimos
 * 12 meses; tocarla abre la página para elegir cualquier otra y periodo.
 */
export function TarjetaCategoria({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, meses, isLoading } = useTotalesMensuales({
    meses: 12,
    categoriasExcluidas: excluidas,
  });
  const mesActual = meses[meses.length - 1];
  const mayor = gastoPorCategoria(movimientos, [mesActual], categorias)
    .filas[0];
  const id = mayor?.categoria ?? null;
  const { colorDe } = useColoresCategorias([id]);
  const serie = evolucionCategoria(movimientos, id, meses);
  const actual = serie[serie.length - 1] ?? 0;
  const clasif = clasificarMes(actual, serie.slice(0, -1));
  const nombre =
    id == null ? t('Uncategorized') : (categorias.get(id)?.nombre ?? '');

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-categoria"
      titulo={mayor ? nombre : t('A category')}
      valor={isLoading ? '…' : mayor ? fmt(actual) : '—'}
      frase={
        isLoading
          ? ''
          : mayor
            ? fraseCategoria(
                t,
                actual,
                clasif.referencia.referencia,
                clasif.tipo,
                12,
                fmt,
              )
            : t('Pick any category to see its last 12 months.')
      }
      tono={clasif.tipo === 'puntual' ? 'aviso' : 'neutro'}
      onPress={() =>
        void navigate(id ? `/reports/categoria/${id}` : '/reports/categoria')
      }
    >
      {mayor && (
        <MiniBarras
          valores={serie}
          color={colorDe(id)}
          alto={tamano === 'grande' ? 64 : 40}
          referencia={clasif.referencia.referencia}
        />
      )}
    </TarjetaInforme>
  );
}

/** «Ingresos»: lo que entró este mes frente a un mes normal. */
export function TarjetaIngresos({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, meses, isLoading } = useTotalesMensuales({
    meses: 12,
    categoriasExcluidas: excluidas,
  });
  const ingresos = ingresosGastosPorMes(movimientos, meses, categorias).map(
    m => m.ingresos,
  );
  const actual = ingresos[ingresos.length - 1] ?? 0;

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-ingresos"
      titulo={t('Income')}
      valor={isLoading ? '…' : fmt(actual)}
      frase={
        isLoading || tamano === 'compacto'
          ? ''
          : fraseNominas(t, actual, ingresos.slice(0, -1), fmt)
      }
      onPress={() => void navigate('/reports/nominas')}
    >
      {tamano !== 'compacto' && (
        <MiniBarras
          valores={ingresos}
          color={COLOR_NOMINAS}
          alto={34}
          referencia={mediana(ingresos.filter(v => v > 0))}
        />
      )}
    </TarjetaInforme>
  );
}

/** «Días de margen»: cuántos días vives de dinero que ya ganaste. */
export function TarjetaMargen({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasta = monthUtils.currentMonth();
  const desde = monthUtils.subMonths(hasta, 11);
  const getEdad = useMemo(
    () =>
      createAgeOfMoneySpreadsheet({
        start: desde,
        end: hasta,
        granularity: 'monthly',
      }),
    [desde, hasta],
  );
  const edad = useReport<AgeOfMoneyData>('edad-dinero', getEdad);

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-margen"
      titulo={t('Days of margin')}
      valor={
        !edad
          ? '…'
          : edad.currentAge === null
            ? '—'
            : t('{{count}} days', { count: edad.currentAge })
      }
      tono={edad ? tonoEdad(edad.currentAge) : 'neutro'}
      frase={edad && tamano !== 'compacto' ? fraseEdad(t, edad) : ''}
      onPress={() => void navigate('/reports/edad-dinero')}
    >
      {tamano !== 'compacto' && (
        <MiniBarras
          valores={(edad?.graphData ?? []).map(p => p.ageOfMoney)}
          color={COLOR_EDAD}
          alto={34}
          referencia={30}
        />
      )}
    </TarjetaInforme>
  );
}
